import { Controller } from '@hotwired/stimulus';

// Diacritic-insensitive fold: lowercase + NFD-decompose + strip combining marks
// (U+0300–U+036F) so "belair" matches "Bélair". Known NFD limit: ligatures like
// œ/æ do not decompose, so "oeuf" won't match "œuf".
const fold = (s) =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

export default class extends Controller {
  static targets = [
    'input',
    'dropdown',
    'hiddenInput',
    'trigger',
    'selected',
    'option',
    'noResults',
    'groupHeader',
    'listbox',
    'searchIcon',
  ];

  connect() {
    this.clickOutsideHandler = this.clickOutside.bind(this);
    document.addEventListener('click', this.clickOutsideHandler);
  }

  disconnect() {
    document.removeEventListener('click', this.clickOutsideHandler);
  }

  toggle(event) {
    event.preventDefault();
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.dropdownTarget.classList.remove('hidden');
    this.triggerTarget.setAttribute('aria-expanded', 'true');
    this.inputTarget.value = '';
    this.inputTarget.focus();
    this.showAllOptions();
  }

  close() {
    this.dropdownTarget.classList.add('hidden');
    this.triggerTarget.setAttribute('aria-expanded', 'false');
    this.focusedIndex = -1;
    this.updateFocusedOption();
  }

  get isOpen() {
    return !this.dropdownTarget.classList.contains('hidden');
  }

  search() {
    const query = fold(this.inputTarget.value).trim();
    let visibleCount = 0;

    this.optionTargets.forEach((option) => {
      const text = fold(option.textContent);
      const match = query === '' || text.includes(query);
      option.classList.toggle('hidden', !match);
      if (match) visibleCount++;
    });

    this.updateGroupHeaders();

    if (this.hasNoResultsTarget) {
      this.noResultsTarget.classList.toggle('hidden', visibleCount > 0);
    }
  }

  updateGroupHeaders() {
    const listItems = Array.from(this.listboxTarget.children);
    let currentHeader = null;
    let hasVisibleOption = false;

    listItems.forEach((item) => {
      if (this.groupHeaderTargets.includes(item)) {
        if (currentHeader) {
          currentHeader.classList.toggle('hidden', !hasVisibleOption);
        }
        currentHeader = item;
        hasVisibleOption = false;
      } else if (this.optionTargets.includes(item)) {
        if (!item.classList.contains('hidden')) {
          hasVisibleOption = true;
        }
      }
    });

    if (currentHeader) {
      currentHeader.classList.toggle('hidden', !hasVisibleOption);
    }

    // Hide separators if either adjacent group has no visible items
    listItems.forEach((item, index) => {
      if (item.getAttribute('aria-hidden') === 'true' && item.tagName === 'LI') {
        const prevGroupHasItems = this.hasVisibleItemsBefore(listItems, index);
        const nextGroupHasItems = this.hasVisibleItemsAfter(listItems, index);
        item.classList.toggle('hidden', !prevGroupHasItems || !nextGroupHasItems);
      }
    });
  }

  hasVisibleItemsBefore(items, separatorIndex) {
    for (let i = separatorIndex - 1; i >= 0; i--) {
      if (this.groupHeaderTargets.includes(items[i])) return false;
      if (this.optionTargets.includes(items[i]) && !items[i].classList.contains('hidden'))
        return true;
    }
    return false;
  }

  hasVisibleItemsAfter(items, separatorIndex) {
    for (let i = separatorIndex + 1; i < items.length; i++) {
      if (this.groupHeaderTargets.includes(items[i])) continue;
      if (this.optionTargets.includes(items[i]) && !items[i].classList.contains('hidden'))
        return true;
      if (items[i] === this.noResultsTarget) return false;
    }
    return false;
  }

  showAllOptions() {
    this.optionTargets.forEach((option) => option.classList.remove('hidden'));
    this.groupHeaderTargets.forEach((header) => header.classList.remove('hidden'));
    Array.from(this.listboxTarget.children).forEach((item) => {
      if (item.getAttribute('aria-hidden') === 'true' && item.tagName === 'LI') {
        item.classList.remove('hidden');
      }
    });
    if (this.hasNoResultsTarget) {
      this.noResultsTarget.classList.add('hidden');
    }
  }

  select(event) {
    const option = event.currentTarget;
    const value = option.dataset.value;
    const name =
      option.querySelector('[data-contractor-combobox-target="optionName"]')?.textContent?.trim() ||
      '';

    // Update hidden input
    this.hiddenInputTarget.value = value;

    // Update trigger display
    this.selectedTarget.textContent = name;
    this.selectedTarget.classList.remove('text-neutral-500');
    this.selectedTarget.classList.add('text-neutral-900');

    // Update trigger padding (remove search icon space)
    this.triggerTarget.classList.remove('pl-9');
    this.triggerTarget.classList.add('pl-3');

    // Hide search icon in trigger if present
    if (this.hasSearchIconTarget) {
      this.searchIconTarget.classList.add('hidden');
    }

    // Update selected state on options
    this.optionTargets.forEach((opt) => {
      const isSelected = opt.dataset.value === value;
      opt.setAttribute('aria-selected', String(isSelected));

      // Toggle checkmark visibility
      const checkmark = opt.querySelector('[data-contractor-combobox-target="checkmark"]');
      if (checkmark) {
        checkmark.classList.toggle('hidden', !isSelected);
      }
    });

    this.close();

    // Dispatch change event for trade-validator
    this.hiddenInputTarget.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleTriggerKeydown(event) {
    if (['ArrowDown', 'Enter', ' '].includes(event.key)) {
      event.preventDefault();
      if (!this.isOpen) this.open();
    }
  }

  handleKeydown(event) {
    const visibleOptions = this.optionTargets.filter((opt) => !opt.classList.contains('hidden'));

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.focusedIndex = Math.min((this.focusedIndex ?? -1) + 1, visibleOptions.length - 1);
        this.updateFocusedOption(visibleOptions);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusedIndex = Math.max((this.focusedIndex ?? 0) - 1, 0);
        this.updateFocusedOption(visibleOptions);
        break;
      case 'Enter':
        event.preventDefault();
        if (this.focusedIndex >= 0 && visibleOptions[this.focusedIndex]) {
          visibleOptions[this.focusedIndex].click();
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.close();
        this.triggerTarget.focus();
        break;
    }
  }

  updateFocusedOption(visibleOptions) {
    const options = visibleOptions || this.optionTargets;
    options.forEach((opt, index) => {
      if (index === this.focusedIndex) {
        opt.classList.add('bg-primary-50');
        opt.scrollIntoView({ block: 'nearest' });
        if (this.hasListboxTarget) {
          this.listboxTarget.setAttribute('aria-activedescendant', opt.id || '');
        }
      } else {
        opt.classList.remove('bg-primary-50');
      }
    });
  }

  clickOutside(event) {
    if (this.isOpen && !this.element.contains(event.target)) {
      this.close();
    }
  }
}
